import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { 
  TrendingDown,
  Shield,
  DollarSign,
  CheckCircle,
  AlertTriangle,
  BarChart3,
  Clock,
  Award,
  Target,
  Zap,
  FileText,
  Phone
} from 'lucide-react';

const ReduceClaimDenials = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <Helmet>
        <title>Reduce Claim Denials in Healthcare | Cut 35-45% | MDeRCM</title>
        <meta 
          name="description" 
          content="Proven strategies to reduce claim denials 35-45%. AI-powered claim auditing, denial prevention & appeals management for medical practices." 
        />
        <meta 
          name="keywords" 
          content="reduce claim denials in healthcare billing, prevent medical claim denials, denial reduction strategies, claim denial management, reduce denial rates, improve claim acceptance, healthcare billing denials" 
        />
        <meta property="og:title" content="Reduce Claim Denials in Healthcare | Cut 35-45% | MDeRCM" />
        <meta property="og:description" content="Reduce claim denials 35-45% with AI-powered auditing and appeals management." />
        <link rel="canonical" href="https://mdercm.com/reduce-claim-denials-healthcare-billing" />
      </Helmet>

      <main style={{ minHeight: '100vh', backgroundColor: '#ffffff', fontFamily: "'Poppins', sans-serif" }}>
        
        {/* Hero Section */}
        <section style={{
          background: 'linear-gradient(135deg, #dc2626 0%, #ea580c 100%)',
          color: '#ffffff',
          padding: isMobile ? '60px 20px' : '80px 40px'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <div style={{ maxWidth: '56rem', margin: '0 auto', textAlign: 'center' }}>
              <div style={{
                display: 'inline-block',
                backgroundColor: 'rgba(255,255,255,0.2)',
                padding: '8px 20px',
                borderRadius: '20px',
                fontSize: '0.875rem',
                fontWeight: '600',
                marginBottom: '20px'
              }}>
                📉 Industry-Leading Denial Reduction
              </div>
              
              <h1 style={{
                fontSize: isMobile ? '2rem' : '3rem',
                fontWeight: 'bold',
                marginBottom: '24px',
                lineHeight: '1.2'
              }}>
                Reduce Claim Denials in Healthcare Billing by 35-45%
              </h1>
              
              <p style={{
                fontSize: isMobile ? '1.125rem' : '1.25rem',
                marginBottom: '32px',
                opacity: 0.95,
                lineHeight: '1.75'
              }}>
                Stop losing revenue to preventable claim denials. Our proven 3-phase approach identifies root causes, recovers denied claims, and prevents future denials—saving practices $50,000-$150,000 annually.
              </p>

              {/* Key Stats */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
                gap: '20px',
                marginTop: '40px'
              }}>
                {[
                  { icon: TrendingDown, value: '35-45%', label: 'Denial Reduction' },
                  { icon: DollarSign, value: '$50K-$150K', label: 'Annual Recovery' },
                  { icon: Clock, value: '24-48hrs', label: 'Appeal Turnaround' },
                  { icon: Award, value: '75-85%', label: 'Appeal Success' }
                ].map((stat, idx) => (
                  <div key={idx} style={{
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    padding: '20px',
                    borderRadius: '12px',
                    textAlign: 'center'
                  }}>
                    <stat.icon size={32} style={{ marginBottom: '12px' }} />
                    <div style={{ fontSize: '1.875rem', fontWeight: 'bold', marginBottom: '4px' }}>
                      {stat.value}
                    </div>
                    <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: '16px',
                justifyContent: 'center',
                marginTop: '40px'
              }}>
                <Link to="/free-trial" style={{
                  textDecoration: 'none',
                  backgroundColor: '#ffffff',
                  color: '#dc2626',
                  padding: '16px 32px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '1rem',
                  display: 'inline-block',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                >
                  Get Free Denial Analysis →
                </Link>
                
                <a href="tel:+15103566069" style={{
                  textDecoration: 'none',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: '#ffffff',
                  padding: '16px 32px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '1rem',
                  display: 'inline-block',
                  border: '2px solid rgba(255,255,255,0.3)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.3)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
                >
                  📞 +1 (510) 356-6069
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Introduction */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#ffffff' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <p style={{
              fontSize: '1.125rem',
              lineHeight: '1.8',
              color: '#374151',
              marginBottom: '24px'
            }}>
              Claim denials are one of the most costly problems in healthcare billing. The average practice loses 10-15% of potential revenue to denied claims, with industry data showing that <strong style={{ color: '#dc2626' }}>63% of denied claims are never reworked</strong>—representing millions in lost revenue across the healthcare system.
            </p>
            <p style={{
              fontSize: '1.125rem',
              lineHeight: '1.8',
              color: '#374151',
              marginBottom: '24px'
            }}>
              The good news? Most claim denials are preventable. With the right strategies, expert oversight, and proactive systems, practices can <strong style={{ color: '#dc2626' }}>reduce claim denials in healthcare billing by 35-45%</strong> while recovering 65-75% of previously denied claims.
            </p>
            <p style={{
              fontSize: '1.125rem',
              lineHeight: '1.8',
              color: '#374151'
            }}>
              Our comprehensive denial reduction program addresses the root causes of denials, implements proven prevention strategies, and aggressively recovers denied revenue—delivering measurable results within 60-90 days.
            </p>
          </div>
        </section>

        {/* The Cost of Denials Section */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#fef2f2' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '16px',
              color: '#111827'
            }}>
              The True Cost of Claim Denials
            </h2>
            <p style={{
              fontSize: '1.125rem',
              textAlign: 'center',
              color: '#dc2626',
              marginBottom: '48px',
              maxWidth: '48rem',
              margin: '0 auto 48px auto',
              fontWeight: '600'
            }}>
              It's far more expensive than you realize
            </p>

            <div style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              padding: isMobile ? '32px 24px' : '48px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
              marginBottom: '40px'
            }}>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                marginBottom: '24px',
                color: '#dc2626',
                textAlign: 'center'
              }}>
                Example: Practice with 1,000 claims per month
              </h3>

              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                gap: '24px',
                marginBottom: '32px'
              }}>
                <div style={{
                  backgroundColor: '#fef2f2',
                  padding: '24px',
                  borderRadius: '12px',
                  border: '2px solid #fecaca'
                }}>
                  <div style={{ fontSize: '0.875rem', color: '#dc2626', marginBottom: '8px', fontWeight: '600' }}>
                    WITHOUT Denial Management
                  </div>
                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}>
                    <li style={{ fontSize: '0.9375rem', color: '#374151' }}>
                      • Denial rate: <strong>10%</strong> (100 claims/month)
                    </li>
                    <li style={{ fontSize: '0.9375rem', color: '#374151' }}>
                      • Avg reimbursement: <strong>$150/claim</strong>
                    </li>
                    <li style={{ fontSize: '0.9375rem', color: '#374151' }}>
                      • Denials never worked: <strong>63%</strong>
                    </li>
                    <li style={{ fontSize: '0.9375rem', color: '#374151' }}>
                      • Recovery rate on worked denials: <strong>40%</strong>
                    </li>
                  </ul>
                  <div style={{
                    marginTop: '16px',
                    paddingTop: '16px',
                    borderTop: '2px solid #fecaca'
                  }}>
                    <div style={{ fontSize: '0.875rem', color: '#dc2626', marginBottom: '4px' }}>
                      Monthly Lost Revenue:
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#dc2626' }}>
                      $10,650
                    </div>
                    <div style={{ fontSize: '1.125rem', fontWeight: '600', color: '#dc2626', marginTop: '8px' }}>
                      Annual: $127,800
                    </div>
                  </div>
                </div>

                <div style={{
                  backgroundColor: '#ecfdf5',
                  padding: '24px',
                  borderRadius: '12px',
                  border: '2px solid #86efac'
                }}>
                  <div style={{ fontSize: '0.875rem', color: '#059669', marginBottom: '8px', fontWeight: '600' }}>
                    WITH Professional Denial Management
                  </div>
                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}>
                    <li style={{ fontSize: '0.9375rem', color: '#374151' }}>
                      • Denial rate: <strong>5%</strong> (50 claims/month)
                    </li>
                    <li style={{ fontSize: '0.9375rem', color: '#374151' }}>
                      • Avg reimbursement: <strong>$150/claim</strong>
                    </li>
                    <li style={{ fontSize: '0.9375rem', color: '#374151' }}>
                      • Denials never worked: <strong>0%</strong>
                    </li>
                    <li style={{ fontSize: '0.9375rem', color: '#374151' }}>
                      • Recovery rate on all denials: <strong>70%</strong>
                    </li>
                  </ul>
                  <div style={{
                    marginTop: '16px',
                    paddingTop: '16px',
                    borderTop: '2px solid #86efac'
                  }}>
                    <div style={{ fontSize: '0.875rem', color: '#059669', marginBottom: '4px' }}>
                      Monthly Lost Revenue:
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#059669' }}>
                      $2,250
                    </div>
                    <div style={{ fontSize: '1.125rem', fontWeight: '600', color: '#059669', marginTop: '8px' }}>
                      Annual: $27,000
                    </div>
                  </div>
                </div>
              </div>

              <div style={{
                backgroundColor: '#059669',
                color: '#ffffff',
                padding: '24px',
                borderRadius: '12px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '1rem', marginBottom: '8px', opacity: 0.95 }}>
                  💰 Net Annual Revenue Recovery
                </div>
                <div style={{ fontSize: isMobile ? '2rem' : '2.5rem', fontWeight: 'bold' }}>
                  $100,800
                </div>
                <div style={{ fontSize: '0.9375rem', marginTop: '8px', opacity: 0.9 }}>
                  Plus intangible benefits: improved cash flow, reduced staff stress, better payer relationships
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Top Denial Reasons */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#ffffff' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '48px',
              color: '#111827'
            }}>
              Top 8 Reasons Claims Get Denied
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: '20px'
            }}>
              {[
                {
                  reason: '1. Missing or Incorrect Patient Information',
                  percentage: '27%',
                  description: 'Wrong demographics, insurance info, dates of birth',
                  solution: 'Automated eligibility verification, front-desk training'
                },
                {
                  reason: '2. Duplicate Claim Submission',
                  percentage: '17%',
                  description: 'Same claim submitted multiple times to same payer',
                  solution: 'Claim tracking systems, clearinghouse edits'
                },
                {
                  reason: '3. Service Not Covered by Payer',
                  percentage: '14%',
                  description: 'Procedure not in patient\'s benefit plan',
                  solution: 'Pre-authorization, benefits verification'
                },
                {
                  reason: '4. Claim Timely Filing Limit Exceeded',
                  percentage: '12%',
                  description: 'Claim filed after payer deadline (typically 90-365 days)',
                  solution: 'Immediate claim submission, tickler systems'
                },
                {
                  reason: '5. Incorrect or Missing Procedure Codes',
                  percentage: '11%',
                  description: 'Wrong CPT codes, missing modifiers, unbundling',
                  solution: 'Certified coders, claim scrubbing software'
                },
                {
                  reason: '6. Prior Authorization Required',
                  percentage: '9%',
                  description: 'Service required pre-approval from payer',
                  solution: 'Authorization tracking, pre-service verification'
                },
                {
                  reason: '7. Bundling/Unbundling Edits',
                  percentage: '6%',
                  description: 'CCI edits, services included in other procedures',
                  solution: 'Automated coding software, expert review'
                },
                {
                  reason: '8. Medical Necessity Not Established',
                  percentage: '4%',
                  description: 'Documentation doesn\'t support procedure code',
                  solution: 'Provider education, documentation improvement'
                }
              ].map((item, idx) => (
                <div key={idx} style={{
                  backgroundColor: '#fff',
                  padding: '20px',
                  borderRadius: '12px',
                  border: '2px solid #fee2e2',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#dc2626';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(220, 38, 38, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#fee2e2';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '12px'
                  }}>
                    <h3 style={{
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: '#111827',
                      flex: 1
                    }}>
                      {item.reason}
                    </h3>
                    <div style={{
                      backgroundColor: '#dc2626',
                      color: '#ffffff',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      marginLeft: '12px'
                    }}>
                      {item.percentage}
                    </div>
                  </div>
                  <p style={{
                    fontSize: '0.9375rem',
                    color: '#6b7280',
                    marginBottom: '12px',
                    lineHeight: '1.5'
                  }}>
                    {item.description}
                  </p>
                  <div style={{
                    backgroundColor: '#f9fafb',
                    padding: '12px',
                    borderRadius: '6px',
                    borderLeft: '3px solid #059669'
                  }}>
                    <div style={{ fontSize: '0.75rem', color: '#059669', fontWeight: '600', marginBottom: '4px' }}>
                      ✓ OUR SOLUTION:
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#374151' }}>
                      {item.solution}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our 3-Phase Approach */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#fef2f2' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '16px',
              color: '#111827'
            }}>
              Our Proven 3-Phase Denial Reduction System
            </h2>
            <p style={{
              fontSize: '1.125rem',
              textAlign: 'center',
              color: '#6b7280',
              marginBottom: '48px',
              maxWidth: '48rem',
              margin: '0 auto 48px auto'
            }}>
              A comprehensive approach that addresses current denials, prevents future ones, and continuously optimizes
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: '24px'
            }}>
              {[
                {
                  phase: 'PHASE 1',
                  title: 'Rapid Recovery',
                  duration: '0-30 Days',
                  icon: DollarSign,
                  color: '#dc2626',
                  steps: [
                    'Audit all open denials (past 12 months)',
                    'Identify recoverable claims ($15K-$50K+)',
                    'Fast-track appeals on highest-value claims',
                    'Work denials within appeal timeframes',
                    'Recover 65-75% of denied revenue'
                  ],
                  results: 'Typical recovery: $15,000-$50,000 in first 30 days'
                },
                {
                  phase: 'PHASE 2',
                  title: 'Root Cause Elimination',
                  duration: '30-90 Days',
                  icon: Target,
                  color: '#ea580c',
                  steps: [
                    'Analyze denial patterns and trends',
                    'Identify top denial reasons for your practice',
                    'Implement targeted prevention strategies',
                    'Train staff on common denial triggers',
                    'Reduce denial rate by 20-25%'
                  ],
                  results: 'Denial rate drops from 10% to 7.5-8%'
                },
                {
                  phase: 'PHASE 3',
                  title: 'Ongoing Optimization',
                  duration: '90+ Days',
                  icon: TrendingDown,
                  color: '#f97316',
                  steps: [
                    'Continuous monitoring and refinement',
                    'Proactive denial prevention systems',
                    'Monthly denial trend reporting',
                    'Staff training and process updates',
                    'Sustain 35-45% denial reduction'
                  ],
                  results: 'Stable denial rate of 5-6.5% maintained'
                }
              ].map((phase, idx) => (
                <div key={idx} style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '16px',
                  padding: '32px 24px',
                  border: `3px solid ${phase.color}`,
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  position: 'relative'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '-16px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: phase.color,
                    color: '#ffffff',
                    padding: '6px 20px',
                    borderRadius: '20px',
                    fontSize: '0.75rem',
                    fontWeight: '700',
                    letterSpacing: '0.5px'
                  }}>
                    {phase.phase}
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: '8px',
                    marginBottom: '16px'
                  }}>
                    <phase.icon size={48} style={{ color: phase.color }} />
                  </div>

                  <h3 style={{
                    fontSize: '1.5rem',
                    fontWeight: '700',
                    textAlign: 'center',
                    marginBottom: '8px',
                    color: '#111827'
                  }}>
                    {phase.title}
                  </h3>

                  <div style={{
                    textAlign: 'center',
                    fontSize: '0.875rem',
                    color: '#6b7280',
                    marginBottom: '24px',
                    fontWeight: '600'
                  }}>
                    ⏱️ {phase.duration}
                  </div>

                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: '0 0 20px 0'
                  }}>
                    {phase.steps.map((step, stepIdx) => (
                      <li key={stepIdx} style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                        marginBottom: '12px',
                        fontSize: '0.9375rem',
                        color: '#374151'
                      }}>
                        <CheckCircle size={18} style={{ color: phase.color, flexShrink: 0, marginTop: '2px' }} />
                        <span>{step}</span>
                      </li>
                    ))}
                  </ul>

                  <div style={{
                    backgroundColor: '#f9fafb',
                    padding: '16px',
                    borderRadius: '8px',
                    border: `2px solid ${phase.color}20`
                  }}>
                    <div style={{
                      fontSize: '0.75rem',
                      color: phase.color,
                      fontWeight: '700',
                      marginBottom: '6px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      EXPECTED RESULTS:
                    </div>
                    <div style={{ fontSize: '0.9375rem', color: '#111827', fontWeight: '600' }}>
                      {phase.results}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Key Performance Indicators */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#ffffff' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '48px',
              color: '#111827'
            }}>
              How We Measure Success
            </h2>

            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '16px',
              padding: isMobile ? '32px 24px' : '48px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                gap: '2px',
                backgroundColor: '#e5e7eb',
                borderRadius: '12px',
                overflow: 'hidden',
                marginBottom: '24px'
              }}>
                <div style={{ backgroundColor: '#ffffff', padding: '16px', textAlign: 'center', fontWeight: '600', color: '#374151' }}>
                  KPI Metric
                </div>
                <div style={{ backgroundColor: '#fef2f2', padding: '16px', textAlign: 'center', fontWeight: '600', color: '#dc2626' }}>
                  Before
                </div>
                <div style={{ backgroundColor: '#ecfdf5', padding: '16px', textAlign: 'center', fontWeight: '600', color: '#059669' }}>
                  After
                </div>
              </div>

              {[
                { metric: 'Initial Denial Rate', before: '10-15%', after: '3-5%' },
                { metric: 'Denials Recovered', before: '35-40%', after: '65-75%' },
                { metric: 'Days to Work Denials', before: '30-60 days', after: '24-48 hours' },
                { metric: 'Appeal Success Rate', before: '40-45%', after: '75-85%' },
                { metric: 'Denials Never Worked', before: '63%', after: '0%' },
                { metric: 'Annual Revenue Recovery', before: '$22,000', after: '$105,000' }
              ].map((kpi, idx) => (
                <div key={idx} style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                  gap: '2px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  marginBottom: '8px'
                }}>
                  <div style={{
                    backgroundColor: '#ffffff',
                    padding: '16px',
                    color: '#111827',
                    fontWeight: '500',
                    fontSize: '0.9375rem'
                  }}>
                    {kpi.metric}
                  </div>
                  <div style={{
                    backgroundColor: '#fef2f2',
                    padding: '16px',
                    textAlign: 'center',
                    color: '#dc2626',
                    fontWeight: '600',
                    fontSize: '1rem'
                  }}>
                    {kpi.before}
                  </div>
                  <div style={{
                    backgroundColor: '#ecfdf5',
                    padding: '16px',
                    textAlign: 'center',
                    color: '#059669',
                    fontWeight: '600',
                    fontSize: '1rem'
                  }}>
                    {kpi.after}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#f9fafb' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '48px',
              color: '#111827'
            }}>
              Common Questions About Reducing Denials
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {[
                {
                  q: 'How quickly will I see results?',
                  a: 'Phase 1 (Rapid Recovery) delivers immediate results—typically $15,000-$50,000 recovered within the first 30 days from existing denied claims. Denial rate reduction shows measurable improvement within 60-90 days as prevention strategies take effect.'
                },
                {
                  q: 'What is a "normal" denial rate?',
                  a: 'Industry average is 10-15% initial denial rate. However, best-in-class practices with professional denial management maintain 3-5% denial rates. Our goal is to get you to industry-leading performance levels.'
                },
                {
                  q: 'How do you recover denied claims?',
                  a: 'We use a systematic approach: identify root cause, gather supporting documentation, prepare compelling appeals with proper medical justification, submit within deadlines, and follow up persistently. Our specialized team knows payer-specific requirements and appeal processes.'
                },
                {
                  q: 'Can you prevent all denials?',
                  a: 'Not all denials are preventable—some are due to legitimate coverage issues. However, 80-85% of denials are caused by administrative errors and are preventable. Our focus is eliminating these avoidable denials while maximizing recovery on legitimate denials.'
                },
                {
                  q: 'What if my denial rate is already low?',
                  a: 'Even if your denial rate is 5-7%, there\'s still opportunity. We focus on: (1) recovering the denials you do have, (2) improving appeal success rates, (3) reducing time to work denials, and (4) ensuring zero denials go unworked. Plus, low denial rates can still be improved—we routinely help practices achieve 3-4% rates.'
                },
                {
                  q: 'Do you handle all types of denials?',
                  a: 'Yes. We work all denial categories: coding/billing errors, missing information, medical necessity, authorization issues, timely filing, duplicate claims, coordination of benefits, and more. We also handle both initial denials and appeal denials through all levels.'
                }
              ].map((faq, idx) => (
                <div key={idx} style={{
                  backgroundColor: '#ffffff',
                  padding: '24px',
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                  <h3 style={{
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    marginBottom: '12px',
                    color: '#dc2626',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px'
                  }}>
                    <AlertTriangle size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span>{faq.q}</span>
                  </h3>
                  <p style={{
                    fontSize: '1rem',
                    lineHeight: '1.75',
                    color: '#374151',
                    margin: 0,
                    paddingLeft: '28px'
                  }}>
                    {faq.a}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section style={{
          padding: isMobile ? '40px 20px' : '60px 40px',
          background: 'linear-gradient(135deg, #dc2626 0%, #ea580c 100%)',
          color: '#ffffff'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              marginBottom: '16px'
            }}>
              Stop Losing Revenue to Preventable Denials
            </h2>
            <p style={{
              fontSize: '1.125rem',
              marginBottom: '32px',
              opacity: 0.95
            }}>
              Get a free denial analysis and see exactly how much you're losing—and how much we can recover
            </p>

            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '16px',
              justifyContent: 'center',
              marginBottom: '32px'
            }}>
              <Link to="/free-trial" style={{
                textDecoration: 'none',
                backgroundColor: '#ffffff',
                color: '#dc2626',
                padding: '16px 32px',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '1.125rem',
                display: 'inline-block'
              }}>
                Get Free Denial Analysis →
              </Link>
              
              <a href="tel:+15103566069" style={{
                textDecoration: 'none',
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: '#ffffff',
                padding: '16px 32px',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '1.125rem',
                display: 'inline-block',
                border: '2px solid rgba(255,255,255,0.3)'
              }}>
                📞 +1 (510) 356-6069
              </a>
            </div>

            <p style={{
              fontSize: '0.9375rem',
              opacity: 0.9
            }}>
              💰 Average first-year recovery: $50,000-$150,000 • 35-45% denial reduction guaranteed
            </p>
          </div>
        </section>

        {/* Related Services */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#ffffff' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              textAlign: 'center',
              marginBottom: '32px',
              color: '#111827'
            }}>
              Related Solutions
            </h3>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: '20px'
            }}>
              {[
                { title: 'Denial Management Services', link: '/denial-management-services-medical-clinics' },
                { title: 'Small Practice Billing', link: '/medical-billing-outsourcing-small-practices' },
                { title: 'View All Solutions', link: '/pricing' }
              ].map((link, idx) => (
                <Link
                  key={idx}
                  to={link.link}
                  style={{
                    padding: '20px',
                    backgroundColor: '#fef2f2',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    border: '2px solid #fee2e2',
                    transition: 'all 0.3s ease',
                    display: 'block'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#dc2626';
                    e.currentTarget.style.backgroundColor = '#fecaca20';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#fee2e2';
                    e.currentTarget.style.backgroundColor = '#fef2f2';
                  }}
                >
                  <span style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#dc2626'
                  }}>
                    {link.title} →
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

      </main>
    </>
  );
};

export default ReduceClaimDenials;